const HTML_ESCAPE_MAP: Record<string, string> = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	'"': "&quot;",
	"'": "&#039;",
	"\r\n": "\n",
};

const COMBINED_ESCAPE_REGEX = /\r\n|[&<>"']/g;
const HEADING_AND_BLOCKQUOTE_REGEX =
	/^[ \t]*(?:(#{1,6})[ \t]*(.*)|(&gt;)[ \t]*(.*))$/gm;
const BOLD_ITALIC_TRIPLE_REGEX = /\*\*\*(.*?)\*\*\*/g;
const BOLD_REGEX = /\*\*(.*?)\*\*/g;
const ITALIC_REGEX = /\*(.*?)\*/g;
const LIST_ITEM_REGEX = /^(\s*)([*-]|\d+\.) (.*)/;
const HEADING_MATCH_REGEX = /^(\s*)(#+)(\s+.*)$/gm;

const HASHES = ["", "#", "##", "###", "####", "#####", "######"];

/**
 * Simple utility to convert basic Markdown to HTML without external dependencies.
 * Handles headings, lists, bold text, and line breaks.
 */
export function renderMarkdown(markdown: string, baseLevel = 3): string {
	if (!markdown) return "";

	// Performance optimization: Normalize newlines and escape HTML in a single pass.
	// This reduces the number of full-string scans and intermediate string allocations.
	let html = markdown.replace(
		COMBINED_ESCAPE_REGEX,
		(match) => HTML_ESCAPE_MAP[match] || match,
	);

	// Performance optimization: Consolidated heading and blockquote replacements into a single pass.
	// This avoids multiple O(N) scans for structural elements.
	html = html.replace(
		HEADING_AND_BLOCKQUOTE_REGEX,
		(match, hashes, hContent, gt, bContent) => {
			if (hashes) {
				const level = hashes.length;
				const targetLevel = Math.min(6, level + baseLevel - 1);
				return `<h${targetLevel}>${hContent}</h${targetLevel}>`;
			}
			if (gt) {
				return `<blockquote>${bContent}</blockquote>`;
			}
			return match;
		},
	);

	// Performance optimization: Using separate passes for bold and italic
	// to ensure correct nesting behavior and leverage V8's string-based replace.
	// We handle triple markers first to avoid interleaving tags.
	// Fast-path: Skip if no markers are present (common in transcript segments).
	if (html.includes("*")) {
		// Bold + Italic (***text***)
		html = html.replace(
			BOLD_ITALIC_TRIPLE_REGEX,
			"<strong><em>$1</em></strong>",
		);
		// Bold (**text**)
		html = html.replace(BOLD_REGEX, "<strong>$1</strong>");
		// Italic (*text*)
		html = html.replace(ITALIC_REGEX, "<em>$1</em>");
	}

	// Lists (Unordered and Ordered) and Line Breaks
	// Performance optimization: Integrate line-break logic into the list-processing loop
	// to eliminate the final .map() pass over all lines.
	const lines = html.split("\n");
	let inUl = false;
	let inOl = false;
	const processedLines = [];

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		const listMatch = line.match(LIST_ITEM_REGEX);

		if (listMatch) {
			const [_, _indent, bullet, content] = listMatch;
			const isUl = bullet === "*" || bullet === "-";

			if (isUl) {
				if (inOl) {
					processedLines.push("</ol>");
					inOl = false;
				}
				if (!inUl) {
					processedLines.push("<ul>");
					inUl = true;
				}
				processedLines.push(`<li>${content}</li>`);
			} else {
				if (inUl) {
					processedLines.push("</ul>");
					inUl = false;
				}
				if (!inOl) {
					processedLines.push("<ol>");
					inOl = true;
				}
				processedLines.push(`<li>${content}</li>`);
			}
		} else {
			if (inUl) {
				processedLines.push("</ul>");
				inUl = false;
			}
			if (inOl) {
				processedLines.push("</ol>");
				inOl = false;
			}

			// Integrated line break logic
			if (
				line === "" ||
				line.startsWith("<h") ||
				line.startsWith("<blockquote>")
			) {
				processedLines.push(line);
			} else {
				processedLines.push(`${line}<br>`);
			}
		}
	}

	if (inUl) processedLines.push("</ul>");
	if (inOl) processedLines.push("</ol>");

	return processedLines.join("\n");
}

/**
 * Shifts Markdown headings in a string by a given offset.
 * e.g. shiftMarkdownHeadings("# Hello", 1) -> "## Hello"
 */
export function shiftMarkdownHeadings(
	markdown: string,
	offset: number,
): string {
	if (!markdown || offset === 0) return markdown;

	// Performance optimization: Using a single pass replace instead of split/map/join.
	// Also using a pre-calculated HASHES array to avoid repeated string allocations.
	return markdown.replace(
		HEADING_MATCH_REGEX,
		(_, indent: string, hashes: string, content: string) => {
			const newLevel = Math.max(1, Math.min(6, hashes.length + offset));
			return `${indent}${HASHES[newLevel]}${content}`;
		},
	);
}

/**
 * Normalizes Markdown headings so that the highest level heading starts at targetLevel.
 */
export function normalizeMarkdownHeadings(
	markdown: string,
	targetLevel: number,
): string {
	if (!markdown) return markdown;

	// Performance optimization: Find minimum heading level in a single pass using matchAll.
	// This avoids splitting the entire string into an array of lines.
	let minLevel = 7;
	const matches = markdown.matchAll(HEADING_MATCH_REGEX);

	for (const match of matches) {
		minLevel = Math.min(minLevel, match[2].length);
		if (minLevel === 1) break; // Optimization: Cannot be lower than 1
	}

	if (minLevel === 7) return markdown;

	return shiftMarkdownHeadings(markdown, targetLevel - minLevel);
}
